import { Mapping } from 'api/MappingsAPI';
import { Project } from 'modules/customEvents/types';
import {
  FIELDT_TABLE_LAYOUT_WITHOUT_BULK_ACTIONS,
  FIELD_TABLE_COLUMNS_WITHOUTH_BULK_ACTIONS,
  FIELD_TABLE_COLUMNS_WITH_BULK_ACTIONS,
  FIELD_TABLE_LAYOUT_WITH_BULK_ACTIONS,
  FIELD_TYPES_WITH_LOCK_SETTINGS,
  VISIBILITY_FILTERS,
} from 'modules/dataDictionary/constants';
import { DataTypeType, FieldType, GenericDataType, ProjectDataSettings } from 'modules/domains';
import {
  CustomEventValues,
  FieldDataType,
  FieldLockState,
  MappingFieldValues,
  UserFieldValues,
} from './types';

/**
 * Convert visibilty boolean to a string to display within
 * a table and validate against visibility filters value
 * @param isHidden boolean
 * @returns string
 */
export const formatVisibility = (isHidden: boolean) =>
  isHidden ? VISIBILITY_FILTERS.hidden : VISIBILITY_FILTERS.visible;

/**
 * Filter mappings based on search and visibility filters
 */
export const filterMappings = (mappings: Mapping[], search: string, visibility: string[]) => {
  const filtered = [...mappings].filter((mapping) => {
    const { fieldName, isHidden } = mapping;
    const hasNameMatch = fieldName.toLowerCase().includes(search.toLowerCase());
    // if no visibility filter is set, return early based on search field
    if (visibility.length === 0) {
      return hasNameMatch;
    }
    const isVisibilityMatch = visibility.includes(formatVisibility(!!isHidden));

    return hasNameMatch && isVisibilityMatch;
  });

  const sorted = filtered.sort((a, b) => a.fieldName.localeCompare(b.fieldName));

  return sorted;
};

/**
 * Convert a number of days to a display value of either whole year(s) or days
 * @param days number
 * @returns string
 */
export const formatDaysToYears = (days: number) => {
  const isWholeYear = days % 365 === 0;
  const numberOfYears = days / 365;
  const formattedPeriod = isWholeYear
    ? `${numberOfYears} ${numberOfYears === 1 ? 'year' : 'years'}`
    : `${days} days`;
  return formattedPeriod;
};

/** Convert keyword field types to string for consistent display */
export const formatFieldTypeForDisplay = (fieldType: FieldType) =>
  fieldType === FieldType.KEYWORD ? FieldType.STRING : fieldType;

/** Convert string fieldtypes to keyword when posting to API */
export const formatFieldTypeForPost = (fieldType: FieldType) =>
  fieldType === FieldType.STRING ? FieldType.KEYWORD : fieldType;

/** Format field name by prepending parent name when applicable */
export const formatFieldName = (fieldName: string, parentName = '') =>
  parentName ? `${parentName}.${fieldName}` : fieldName;

/** Format field type when creating API payload */
export const createFieldPayload = (
  data: MappingFieldValues,
  projectId: number,
  parentName = '',
) => {
  const { description = '', sampleValue = '' } = data;
  const baseFieldName = formatFieldName(data.fieldName, parentName);
  const fieldType = formatFieldTypeForPost(data.fieldType);
  const isValidFieldType = Object.values(FieldType).includes(fieldType);
  // appends _geo_location to GEO fields
  const fieldName =
    fieldType === FieldType.GEO_LOCATION ? `${baseFieldName}_geo_location` : baseFieldName;

  if (projectId && fieldName && isValidFieldType) {
    return {
      description,
      fieldName,
      fieldType,
      projectId,
      sampleValue,
    };
  }
};

/**
 * Format ancestor and field name for display
 * Accepts a string name with dot notation (ie. 'parent.child.field') and
 * returns an array with the ancestor and field name (ie. ['parent.child', 'field'])
 */
export const formatAncestorAndFieldName = (fieldNames: string[], name: string, type: FieldType) => {
  // field is an ancestor if it is an object
  if (type === FieldType.OBJECT) {
    return [name, ''];
  }
  // Find the field name by the last dot in the string
  const lastIndex = name.lastIndexOf('.');
  const isChild = lastIndex !== -1;
  // Pulls the name and it's ancestors from the string
  const fieldName = isChild ? name.substring(lastIndex) : name;
  const fieldNameAcenstors = isChild ? name.substring(0, lastIndex) : '';
  // Make sure the ancestor is known in the list of field names provided since
  // legacy field names could include a dot in the name
  const isAncestorsAnObject = fieldNames.includes(fieldNameAcenstors);
  const ancestor = isAncestorsAnObject ? fieldNameAcenstors : '';
  return [ancestor, fieldName];
};

/**
 * Create visibility toast success message
 */
export const createSuccessMessage = (fieldCount: number, action: 'hidden' | 'visible') => {
  const prefix = 'Done!';
  const fieldVerb = fieldCount === 1 ? 'field has' : 'fields have';

  switch (action) {
    case 'hidden':
      return `${prefix} ${fieldCount} ${fieldVerb} been hidden`;
    case 'visible':
      return `${prefix} ${fieldCount} ${fieldVerb} been made visible`;
    default:
      return `${prefix} ${fieldCount} ${fieldVerb} changed`;
  }
};

export const createUniqueArray = (array: (string | number)[]) =>
  Array.from(new Set(array)).map((item) => item.toString());

/**
 * Create payload to update Project Settings when hiding or unhiding fields
 */
type PayloadParams = {
  dataType: DataTypeType;
  defaultDataSettings: ProjectDataSettings;
  fields: (string | number)[]; // number not really needed but comes from selection context
};
export const createHideFieldsPayload = ({
  dataType,
  defaultDataSettings,
  fields,
}: PayloadParams) => {
  const { purchaseHiddenFields: purchaseFields = [], userHiddenFields: userFields = [] } =
    defaultDataSettings;

  const purchaseHiddenFields =
    dataType === GenericDataType.Purchase
      ? createUniqueArray([...purchaseFields, ...fields])
      : purchaseFields;

  const userHiddenFields =
    dataType === GenericDataType.User ? createUniqueArray([...userFields, ...fields]) : userFields;

  return {
    dataSettings: {
      ...defaultDataSettings,
      purchaseHiddenFields,
      userHiddenFields,
    },
  };
};

export const createUnhideFieldsPayload = ({
  dataType,
  defaultDataSettings,
  fields,
}: PayloadParams) => {
  const { purchaseHiddenFields: purchaseFields = [], userHiddenFields: userFields = [] } =
    defaultDataSettings;

  const purchaseHiddenFields =
    dataType === GenericDataType.Purchase
      ? purchaseFields.filter((f) => !fields.includes(f))
      : purchaseFields;

  const userHiddenFields =
    dataType === GenericDataType.User ? userFields.filter((f) => !fields.includes(f)) : userFields;

  return {
    dataSettings: {
      ...defaultDataSettings,
      purchaseHiddenFields,
      userHiddenFields,
    },
  };
};

/** Create text that displays remaining characters for input */
export const createRestrictionText = (charCount: number, maxCharacters: number) => {
  const remainingChars = Math.max(maxCharacters - charCount, 0);
  return `${remainingChars}/${maxCharacters} characters`;
};

/** Generate grid layout with or without bulk action cells */
export const createGridLayout = (isBulkActionEnabled = true) =>
  isBulkActionEnabled
    ? FIELD_TABLE_LAYOUT_WITH_BULK_ACTIONS
    : FIELDT_TABLE_LAYOUT_WITHOUT_BULK_ACTIONS;

/** Generate column headers with or without bulk action cells */
export const createColumns = (isBulkActionEnabled = true) =>
  isBulkActionEnabled
    ? FIELD_TABLE_COLUMNS_WITH_BULK_ACTIONS
    : FIELD_TABLE_COLUMNS_WITHOUTH_BULK_ACTIONS;

/**
 * Create text for field lock Popover based on field type and lock state
 * - Not all field types have a lock status
 * - Both Custom Events and Custom Event Fields have a dataType of CustomEvent
 *   so we pass in a FieldDataType to differentiate between the two
 */
export const createFieldLockText = (fieldType?: FieldDataType, fieldLockState?: FieldLockState) => {
  const fieldTypeDetails = FIELD_TYPES_WITH_LOCK_SETTINGS.find(
    (field) => field.fieldType === fieldType,
  );
  if (fieldTypeDetails && fieldLockState) {
    // Create popover text based on field lock state and datatype
    const verb = fieldLockState === FieldLockState.On ? 'will not' : 'will';
    const content = `Unrecognized ${fieldTypeDetails?.noun} ${verb} be created automatically during list uploads and API calls. This behavior can be changed in Data Schema Management Settings.`;
    const label = `${fieldTypeDetails?.label} lock is ${fieldLockState}`;
    return { content, label };
  }
};

/**
 * Convert booleans to strings for use in RadioGroup
 */
export const createDefaultSettings = (project: Project) => {
  /** Get values from Project */
  const {
    allowAutoCustomEventFieldCreation,
    allowAutoUserFieldCreation,
    extendedProjectSettings: { allowNewCustomEvents } = {},
  } = project;

  /**
   * Convert booleans to strings for use in RadioGroup
   * User field value
   *
   * Custom event value (one of three options)
   * 1. Field lock off for both
   * 2. Lock for events, off for fields
   * 3. Lock for both
   *
   * Cannot have lock for fields and not events
   */
  const userFieldValue = allowAutoUserFieldCreation
    ? UserFieldValues.ALLOW_USER
    : UserFieldValues.DROP_USER;

  const customEventIntermediate = allowNewCustomEvents
    ? CustomEventValues.ALLOW_EVENT_AND_FIELDS
    : CustomEventValues.DROP_EVENTS_ALLOW_FIELDS;

  const customEventValue = allowAutoCustomEventFieldCreation
    ? customEventIntermediate
    : CustomEventValues.DROP_BOTH_EVENTS_AND_FIELDS;

  return { userFieldValue, customEventValue };
};

/**
 * Convert strings to booleans when updating settings via POST
 */
export const createSettingsPayload = (
  userValue: UserFieldValues,
  eventValue: CustomEventValues,
) => ({
  // custom event fields
  allowAutoCustomEventFieldCreation: eventValue !== CustomEventValues.DROP_BOTH_EVENTS_AND_FIELDS,
  // user fields
  allowAutoUserFieldCreation: userValue === UserFieldValues.ALLOW_USER,
  // custom events
  allowNewCustomEvents: eventValue === CustomEventValues.ALLOW_EVENT_AND_FIELDS,
});
